import { useOs } from "@mantine/hooks";
import { useGeolocation, type GeolocationState } from "@uidotdev/usehooks";
import { getDistance, isPointWithinRadius } from "geolib";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_CENTER: GeoCoordinates = { latitude: 0, longitude: 0 };
const DEFAULT_RADIUS_KILOMETERS = 10;

export type GeoCoordinates = {
  latitude: NonNullable<GeolocationState["latitude"]>;
  longitude: NonNullable<GeolocationState["longitude"]>;
};

// enableHighAccuracy: false evita o provider de alta precisão, que costuma retornar
// "Response was malformed" (POSITION_UNAVAILABLE) em VMs/ambientes sem GPS
const DEFAULT_POSITION_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 10000,
  maximumAge: 60000
};

export type UseGeoRadiusOptions = {
  /** Ponto de referência para o cálculo do raio. @default DEFAULT_CENTER (mock) */
  center?: GeoCoordinates;
  /** Raio em quilômetros a partir de `center`. @default DEFAULT_RADIUS_KILOMETERS */
  radius?: number;
  /** Se `true`, monitora a posição continuamente (`watchPosition`); senão, obtém uma única vez. */
  watch?: boolean;
  /** Opções nativas repassadas ao `useGeolocation`. @default DEFAULT_POSITION_OPTIONS */
  positionOptions?: PositionOptions;
  /** Localização usada quando o provedor de geolocalização do navegador falha (ex.: bug "Response was malformed" do Chromium). */
  fallbackLocation?: GeoCoordinates;
  /** Intervalo mínimo entre atualizações do modo `watch`, em milissegundos. @default 1000 */
  updateInterval?: number;
};

/**
 * Obtém a localização atual do cliente e determina se ela está dentro de um raio
 * fixo a partir de um ponto de referência (`center`), usando `geolib`.
 *
 * @param options.center Ponto de referência (mock por padrão, sobrescrevível).
 * @param options.radius Raio em quilômetros a partir de `center` (mock por padrão, sobrescrevível).
 * @param options.watch Mantido por compatibilidade; `useGeolocation` monitora a posição continuamente.
 * @param options.positionOptions Opções nativas de geolocalização (precisão, timeout, cache).
 * @param options.fallbackLocation Localização usada caso o navegador falhe ao obter a posição.
 * @param options.updateInterval Intervalo mínimo entre atualizações do modo `watch`, em milissegundos.
 * @returns `location` posição atual do cliente (ou `null` enquanto não obtida/indisponível).
 * @returns `center` ponto de referência usado no cálculo.
 * @returns `radius` raio em quilômetros usado no cálculo.
 * @returns `loading` indica se a posição ainda está sendo obtida.
 * @returns `error` erro de geolocalização, se houver.
 * @returns `isWithinRadius` `true` se o cliente estiver dentro do raio a partir de `center`.
 * @returns `distanceToCenter` distância em metros entre o cliente e `center` (`null` sem localização).
 * @returns `isPointInsideRadius` checa se um ponto arbitrário está dentro do raio a partir do cliente.
 * @returns `getDistanceToPoint` calcula a distância em metros entre o cliente e um ponto arbitrário.
 */
export function useGeoRadius(options: UseGeoRadiusOptions = {}) {
  const os = useOs();
  const isMobile = os === "android" || os === "ios" || os === "undetermined";
  const positionOptions = useMemo(() => ({ ...DEFAULT_POSITION_OPTIONS, ...(isMobile ? { enableHighAccuracy: true } : {}) }), [isMobile]);
  const { center = DEFAULT_CENTER, radius = DEFAULT_RADIUS_KILOMETERS, watch = true, positionOptions: customPositionOptions, fallbackLocation, updateInterval = 1000 } = options;
  const resolvedPositionOptions = useMemo<PositionOptions>(() => ({ ...positionOptions, ...customPositionOptions }), [customPositionOptions, positionOptions]);
  const geolocation = useGeolocation(resolvedPositionOptions);
  const radiusInMeters = radius * 1000;

  const [location, setLocation] = useState<GeoCoordinates | null>(null);
  const lastUpdateAtRef = useRef<number | null>(null);

  const isAllowedToProceed = () => {
    // if (radius > 0) {
    //   if (!location) {
    //     toast.error("Localização não encontrada!");
    //     return false;
    //   }
    //   if (!isWithinRadius) {
    //     toast.error("Você não está dentro do raio permitido!");
    //     return false;
    //   }
    // }

    return true;
  };

  const isPointInsideRadius = useCallback(
    (point: GeoCoordinates) => {
      if (!location) return false;
      return isPointWithinRadius(location, point, radiusInMeters);
    },
    [location, radiusInMeters]
  );

  const getDistanceToPoint = useCallback(
    (point: GeoCoordinates) => {
      if (!location) return null;
      return getDistance(location, point);
    },
    [location]
  );

  // Distância e checagem do cliente em relação ao ponto fixo (`center`)
  const distanceToCenter = location ? getDistance(location, center) : null;
  const isWithinRadius = location ? isPointWithinRadius(location, center, radiusInMeters) : false;
  const isLoading = geolocation.loading && !location;
  const isError = !!geolocation.error;
  const error = geolocation.error;

  useEffect(() => {
    if (geolocation.latitude === null || geolocation.longitude === null) return;

    const now = Date.now();
    if (watch && lastUpdateAtRef.current !== null && now - lastUpdateAtRef.current < updateInterval) return;

    lastUpdateAtRef.current = now;
    setLocation({ latitude: geolocation.latitude, longitude: geolocation.longitude });
  }, [geolocation.latitude, geolocation.longitude, updateInterval, watch]);

  useEffect(() => {
    if (geolocation.error && fallbackLocation && !location) setLocation(fallbackLocation);
  }, [fallbackLocation, geolocation.error, location]);

  return {
    location,
    center,
    isLoading,
    error,
    isError,
    isWithinRadius,
    radius,
    distanceToCenter,
    distanceToCenterKm: distanceToCenter ? (distanceToCenter / 1000).toFixed(2) : "0.00",
    isPointInsideRadius,
    isAllowedToProceed,
    getDistanceToPoint
  };
}
